import { useState, useEffect } from 'react';
import { conductores, auth } from '../services/api';
import { useToast } from '../context/ToastContext';
import { TrashIcon, PencilIcon, PlusIcon, XMarkIcon, ChevronUpIcon, ChevronDownIcon, SunIcon, MoonIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import ConfirmModal from '../components/ConfirmModal';

export default function Conductores() {
    const [conductoresList, setConductoresList] = useState([]);
    const [auxData, setAuxData] = useState({ grupos_vacaciones: [], grupos_libranzas: [] });
    const [workDaysStats, setWorkDaysStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingConductor, setEditingConductor] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        grupo_vacaciones_id: '',
        grupo_libranza_id: '',
        turno: '',
        tipo: 'bei',
        rol: 'Conductor',
        tipo_calendario: 'ciclo',
        es_correturnos: false
    });
    const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'asc' });
    const [tipoFilter, setTipoFilter] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, nombre: '' });
    const [revokeConfirm, setRevokeConfirm] = useState({ isOpen: false, id: null, nombre: '' });

    const { success, error: toastError } = useToast();

    useEffect(() => {
        fetchData();
    }, [tipoFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [conductoresData, auxiliaryData, workDaysData] = await Promise.all([
                conductores.getAll(null, tipoFilter || null),
                conductores.getAuxiliaryData(tipoFilter || null),
                conductores.getWorkDaysStats(2026)
            ]);
            setConductoresList(conductoresData);
            setAuxData(auxiliaryData);
            const statsMap = {};
            workDaysData.forEach(stat => {
                statsMap[stat.conductor_id] = stat.dias_trabajo;
            });
            setWorkDaysStats(statsMap);
        } catch (error) {
            console.error('Error in fetchData:', error);
            toastError('Error al cargar datos: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                nombre: formData.nombre,
                grupo_vacaciones_id: formData.grupo_vacaciones_id || null,
                grupo_libranza_id: formData.grupo_libranza_id || null,
                turno: formData.turno || null,
                tipo: formData.tipo || 'bei',
                rol: formData.rol || 'Conductor',
                tipo_calendario: formData.tipo_calendario || 'ciclo',
                es_correturnos: formData.es_correturnos || false
            };

            if (editingConductor) {
                await conductores.update(editingConductor.id, payload);
                success('Conductor actualizado');
            } else {
                await conductores.create(payload);
                success('Conductor creado');
            }
            setShowModal(false);
            fetchData();
        } catch (error) {
            console.error(error);
            toastError(error.message || 'Error al guardar');
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedConductores = [...conductoresList].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <div className="w-4 h-4" />;
        return sortConfig.direction === 'asc'
            ? <ChevronUpIcon className="w-4 h-4 text-cyan-400" />
            : <ChevronDownIcon className="w-4 h-4 text-cyan-400" />;
    };

    const handleDelete = (id, nombre) => {
        setDeleteConfirm({ isOpen: true, id, nombre });
    };

    const handleRevoke = (id, nombre) => {
        setRevokeConfirm({ isOpen: true, id, nombre });
    };

    const confirmRevoke = async () => {
        const id = revokeConfirm.id;
        if (!id) return;
        try {
            await auth.revokeUserSessions(id);
            success('Sesiones revocadas exitosamente');
        } catch (error) {
            console.error(error);
            toastError(error.message || 'Error al revocar sesiones');
        }
    };

    const confirmDelete = async () => {
        const id = deleteConfirm.id;
        if (!id) return;
        try {
            await conductores.delete(id);
            success('Conductor eliminado');
            fetchData();
        } catch (error) {
            console.error(error);
            toastError('Error al eliminar conductor');
        }
    };

    const openModal = (conductor = null) => {
        if (conductor) {
            setEditingConductor(conductor);
            setFormData({
                nombre: conductor.nombre,
                grupo_vacaciones_id: conductor.grupo_vacaciones_id || '',
                grupo_libranza_id: conductor.grupo_libranza_id || '',
                turno: conductor.turno || '',
                tipo: conductor.tipo || 'bei',
                rol: conductor.rol || 'Conductor',
                tipo_calendario: conductor.tipo_calendario || 'ciclo',
                es_correturnos: !!conductor.es_correturnos
            });
        } else {
            setEditingConductor(null);
            setFormData({
                nombre: '',
                grupo_vacaciones_id: '',
                grupo_libranza_id: '',
                turno: '',
                tipo: 'bei',
                rol: 'Conductor',
                tipo_calendario: 'ciclo',
                es_correturnos: false
            });
        }
        setShowModal(true);
    };

    if (loading) return <div className="p-4 text-white">Cargando...</div>;

    return (
        <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-white">Gestión de Conductores</h1>
                <div className="flex items-center gap-3">
                    {/* Tipo Filter */}
                    <div className="flex rounded-lg overflow-hidden border border-white/10">
                        {[{ val: '', label: 'Todos' }, { val: 'bei', label: '🚌 BEI' }, { val: 'servicio', label: '🚍 Servicio' }].map(opt => (
                            <button
                                key={opt.val}
                                onClick={() => { setTipoFilter(opt.val); }}
                                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                                    tipoFilter === opt.val
                                        ? 'bg-cyan-500/30 text-cyan-300'
                                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => openModal()}
                        className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg shadow-cyan-500/20"
                    >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Nuevo Conductor
                    </button>
                </div>
            </div>

            <div className="bg-white/5 rounded-xl border border-white/10 shadow-xl overflow-hidden backdrop-blur-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 bg-white/5">
                                <th
                                    className="p-3 sm:p-4 font-semibold text-gray-300 cursor-pointer hover:bg-white/5 transition-colors select-none group"
                                    onClick={() => handleSort('nombre')}
                                >
                                    <div className="flex items-center gap-2">
                                        Nombre / Rol
                                        <SortIcon columnKey="nombre" />
                                    </div>
                                </th>
                                <th
                                    className="p-3 sm:p-4 font-semibold text-gray-300 cursor-pointer hover:bg-white/5 transition-colors select-none group hidden lg:table-cell"
                                    onClick={() => handleSort('tipo_calendario')}
                                >
                                    <div className="flex items-center gap-2">
                                        Calendario
                                        <SortIcon columnKey="tipo_calendario" />
                                    </div>
                                </th>
                                <th
                                    className="p-3 sm:p-4 font-semibold text-gray-300 cursor-pointer hover:bg-white/5 transition-colors select-none group hidden lg:table-cell"
                                    onClick={() => handleSort('grupo_vacaciones_numero')}
                                >
                                    <div className="flex items-center gap-2">
                                        Grupo Vacaciones
                                        <SortIcon columnKey="grupo_vacaciones_numero" />
                                    </div>
                                </th>
                                <th
                                    className="p-3 sm:p-4 font-semibold text-gray-300 cursor-pointer hover:bg-white/5 transition-colors select-none group hidden lg:table-cell"
                                    onClick={() => handleSort('grupo_libranza_nombre')}
                                >
                                    <div className="flex items-center gap-2">
                                        Grupo Libranzas
                                        <SortIcon columnKey="grupo_libranza_nombre" />
                                    </div>
                                </th>
                                <th
                                    className="p-3 sm:p-4 font-semibold text-gray-300 cursor-pointer hover:bg-white/5 transition-colors select-none group hidden sm:table-cell"
                                    onClick={() => handleSort('turno')}
                                >
                                    <div className="flex items-center gap-2">
                                        Turno (Inicio)
                                        <SortIcon columnKey="turno" />
                                    </div>
                                </th>
                                <th className="p-3 sm:p-4 font-semibold text-gray-300 text-center hidden md:table-cell">Días Trabajo</th>
                                <th className="p-3 sm:p-4 font-semibold text-gray-300 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {sortedConductores.map((conductor) => (
                                <tr key={conductor.id} className="hover:bg-white/5 transition-colors">
                                    <td className="p-3 sm:p-4 font-medium text-white">
                                        <div className="truncate max-w-[180px] sm:max-w-none">
                                            <div className="flex items-center gap-2">
                                                {conductor.nombre}
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${conductor.tipo === 'servicio' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                                                    {conductor.tipo || 'bei'}
                                                </span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase bg-purple-500/20 text-purple-400">
                                                    {conductor.rol || 'Conductor'}
                                                </span>
                                                {conductor.es_correturnos && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-orange-500/20 text-orange-400" title="Correturnos">
                                                        CT
                                                    </span>
                                                )}
                                            </div>
                                            {/* Mobile only details */}
                                            <div className="sm:hidden text-xs text-gray-500 mt-1 font-normal">
                                                {conductor.grupo_vacaciones_numero ? `G${conductor.grupo_vacaciones_numero}` : ''}
                                                {conductor.grupo_libranza_nombre ? ` • ${conductor.grupo_libranza_nombre}` : ''}
                                                {conductor.turno ? ` • ${conductor.turno}` : ''}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3 sm:p-4 text-gray-400 hidden lg:table-cell">
                                        {conductor.tipo_calendario === 'manual' ? (
                                            <span className="flex items-center gap-1.5 text-cyan-400 font-bold">
                                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                                                MANUAL
                                            </span>
                                        ) : (
                                            <span className="text-gray-500 italic">Automático</span>
                                        )}
                                    </td>
                                    <td className="p-3 sm:p-4 text-gray-400 hidden lg:table-cell">
                                        {conductor.grupo_vacaciones_numero ? `Grupo ${conductor.grupo_vacaciones_numero}` : '-'}
                                    </td>
                                    <td className="p-3 sm:p-4 text-gray-400 hidden lg:table-cell">
                                        {conductor.grupo_libranza_nombre || '-'}
                                    </td>
                                    <td className="p-3 sm:p-4 text-gray-400 hidden sm:table-cell">
                                        {conductor.turno === 'Mañana' && (
                                            <span className="flex items-center gap-2 text-yellow-500">
                                                <SunIcon className="w-5 h-5" /> Mañana
                                            </span>
                                        )}
                                        {conductor.turno === 'Tarde' && (
                                            <span className="flex items-center gap-2 text-orange-400">
                                                <MoonIcon className="w-5 h-5" /> Tarde
                                            </span>
                                        )}
                                        {!conductor.turno && '-'}
                                    </td>
                                    <td className="p-3 sm:p-4 text-center hidden md:table-cell">
                                        {workDaysStats[conductor.id] !== undefined ? (
                                            <span className={`font-bold px-2 py-1 rounded ${workDaysStats[conductor.id] === 199
                                                ? 'text-green-400 bg-green-500/10'
                                                : 'text-red-400 bg-red-500/10'
                                                }`}>
                                                {workDaysStats[conductor.id]}
                                            </span>
                                        ) : (
                                            <span className="text-gray-500">-</span>
                                        )}
                                    </td>
                                    <td className="p-3 sm:p-4 text-right space-x-1 sm:space-x-2">
                                        <button
                                            onClick={() => openModal(conductor)}
                                            className="p-2 text-cyan-400 hover:bg-cyan-400/10 rounded-lg transition-colors"
                                            title="Editar"
                                        >
                                            <PencilIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(conductor.id, conductor.nombre)}
                                            className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                            title="Eliminar"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleRevoke(conductor.id, conductor.nombre)}
                                            className="p-2 text-yellow-400 hover:bg-yellow-400/10 rounded-lg transition-colors"
                                            title="Revocar sesión"
                                        >
                                            <ArrowPathIcon className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {conductoresList.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="p-8 text-center text-gray-500">
                                        No hay conductores registrados
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-[#1a1a3e] border border-white/10 rounded-xl shadow-2xl max-w-md w-full animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-6 border-b border-white/10">
                            <h2 className="text-xl font-bold text-white">
                                {editingConductor ? 'Editar Conductor' : 'Nuevo Conductor'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Nombre Completo
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                                    placeholder="Ej: Juan Pérez"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Rol / Función
                                </label>
                                <select
                                    value={formData.rol}
                                    onChange={(e) => {
                                        const newRol = e.target.value;
                                        const newTipoCal = ['Reducción', 'Revisor', 'Revisor/Conductor'].includes(newRol) ? 'manual' : 'ciclo';
                                        setFormData({
                                            ...formData,
                                            rol: newRol,
                                            tipo_calendario: newTipoCal,
                                            grupo_vacaciones_id: newTipoCal === 'manual' ? '' : formData.grupo_vacaciones_id,
                                            grupo_libranza_id: newTipoCal === 'manual' ? '' : formData.grupo_libranza_id
                                        });
                                    }}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                                >
                                    <option value="Conductor" className="bg-[#1a1a3e]">🚌 Conductor Standard</option>
                                    <option value="Reducción" className="bg-[#1a1a3e]">📉 Reducción de Jornada</option>
                                    <option value="Revisor" className="bg-[#1a1a3e]">🧐 Revisor</option>
                                    <option value="Revisor/Conductor" className="bg-[#1a1a3e]">🔄 Revisor / Conductor</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Tipo de Calendario (Asignado por Rol)
                                </label>
                                <div className="flex gap-3 opacity-80 pointer-events-none cursor-not-allowed">
                                    {[{ val: 'ciclo', label: '🔄 Automático' }, { val: 'manual', label: '🎨 Manual' }].map(opt => (
                                        <button
                                            key={opt.val}
                                            type="button"
                                            className={`flex-1 px-4 py-2 rounded-lg border font-medium transition-all ${
                                                formData.tipo_calendario === opt.val
                                                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-300'
                                                    : 'border-white/10 text-gray-400'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                {formData.tipo_calendario === 'manual' && (
                                    <p className="text-[10px] text-cyan-500 mt-1 cursor-default">
                                        ✨ Al ser MANUAL, se desactivan los grupos de vacaciones y libranza. El usuario pintará su calendario.
                                    </p>
                                )}
                            </div>

                            <div className={formData.tipo_calendario === 'manual' ? 'opacity-30 pointer-events-none' : ''}>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Grupo de Vacaciones
                                </label>
                                <select
                                    disabled={formData.tipo_calendario === 'manual'}
                                    value={formData.grupo_vacaciones_id}
                                    onChange={(e) => setFormData({ ...formData, grupo_vacaciones_id: e.target.value })}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all disabled:cursor-not-allowed"
                                >
                                    <option value="" className="bg-[#1a1a3e]">Sin Asignar</option>
                                    {auxData.grupos_vacaciones.map(g => (
                                        <option key={g.id} value={g.id} className="bg-[#1a1a3e]">
                                            Grupo {g.numero} ({g.periodo_vacaciones})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className={formData.tipo_calendario === 'manual' ? 'opacity-30 pointer-events-none' : ''}>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Grupo de Libranzas
                                </label>
                                <select
                                    disabled={formData.tipo_calendario === 'manual'}
                                    value={formData.grupo_libranza_id}
                                    onChange={(e) => setFormData({ ...formData, grupo_libranza_id: e.target.value })}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all disabled:cursor-not-allowed"
                                >
                                    <option value="" className="bg-[#1a1a3e]">Sin Asignar</option>
                                    {auxData.grupos_libranzas.map(g => (
                                        <option key={g.id} value={g.id} className="bg-[#1a1a3e]">
                                            {g.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Turno (Inicio Enero 1ª Semana)
                                </label>
                                <select
                                    value={formData.turno}
                                    onChange={(e) => setFormData({ ...formData, turno: e.target.value })}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                                >
                                    <option value="" className="bg-[#1a1a3e]">Sin Asignar</option>
                                    <option value="Mañana" className="bg-[#1a1a3e]">Mañana</option>
                                    <option value="Tarde" className="bg-[#1a1a3e]">Tarde</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">El turno rotará semanalmente (de Sábado a Domingo).</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Tipo de Servicio
                                </label>
                                <div className="flex gap-3">
                                    {[{ val: 'bei', label: '🚌 BEI' }, { val: 'servicio', label: '🚍 Servicio' }].map(opt => (
                                        <button
                                            key={opt.val}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, tipo: opt.val })}
                                            className={`flex-1 px-4 py-2 rounded-lg border font-medium transition-all ${
                                                formData.tipo === opt.val
                                                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-300'
                                                    : 'border-white/10 text-gray-400 hover:bg-white/10'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="es_correturnos"
                                    checked={formData.es_correturnos}
                                    onChange={(e) => setFormData({ ...formData, es_correturnos: e.target.checked })}
                                    className="w-4 h-4 rounded border-white/20 bg-white/5 text-cyan-500 focus:ring-cyan-500"
                                />
                                <label htmlFor="es_correturnos" className="text-sm text-gray-300">
                                    Es Correturnos <span className="text-xs text-gray-500">(cambio de turno sin previo aviso)</span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg shadow-cyan-500/20"
                                >
                                    {editingConductor ? 'Guardar Cambios' : 'Crear Conductor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, nombre: '' })}
                onConfirm={confirmDelete}
                title="Eliminar Conductor"
                message={`¿Estás seguro de que deseas eliminar a "${deleteConfirm.nombre}"?`}
                confirmText="Eliminar"
                variant="danger"
            />

            <ConfirmModal
                isOpen={revokeConfirm.isOpen}
                onClose={() => setRevokeConfirm({ isOpen: false, id: null, nombre: '' })}
                onConfirm={confirmRevoke}
                title="Revocar sesiones"
                message={`¿Revocar todas las sesiones del usuario "${revokeConfirm.nombre}"?`}
                confirmText="Revocar"
                variant="warning"
            />
        </div>
    );
}
